import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Filter, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LeaseFilters } from '../types/leases.types';

const leaseFiltersSchema = z.object({
    status: z.enum(['ACTIVE', 'COMPLETED', 'CANCELED', 'PENDING', 'all']).optional(),
    propertyUUID: z.string().optional(),
    tenantUUID: z.string().optional(),
    managerUUID: z.string().optional(),
    ownerUUID: z.string().optional(),
    search: z.string().optional(),
    page: z.number().optional(),
    size: z.number().optional(),
});

type LeaseFiltersValues = z.infer<typeof leaseFiltersSchema>;

interface LeasesFiltersProps {
    filters: LeaseFilters;
    onChange: (filters: LeaseFilters) => void;
    properties?: Array<{ uuid: string; name: string }>;
    tenants?: Array<{ uuid: string; firstName: string; lastName: string }>;
    managers?: Array<{ uuid: string; firstName: string; lastName: string }>;
    owners?: Array<{ uuid: string; firstName: string; lastName: string }>;
}

const DEFAULT_FILTERS: LeaseFilters = {
    status: undefined,
    propertyUUID: undefined,
    tenantUUID: undefined,
    managerUUID: undefined,
    ownerUUID: undefined,
    search: undefined,
    page: 0,
    size: 10,
};

const LeasesFilters = ({
    filters,
    onChange,
    properties = [],
    tenants = [],
    managers = [],
    owners = []
}: LeasesFiltersProps) => {
    const { t } = useTranslation();
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);

    const form = useForm<LeaseFiltersValues>({
        resolver: zodResolver(leaseFiltersSchema),
        defaultValues: {
            status: filters.status || undefined,
            propertyUUID: filters.propertyUUID || undefined,
            tenantUUID: filters.tenantUUID || undefined,
            managerUUID: filters.managerUUID || undefined,
            ownerUUID: filters.ownerUUID || undefined,
            search: filters.search || '',
            page: filters.page || 0,
            size: filters.size || 10,
        },
    });

    const handleSubmit = (values: LeaseFiltersValues) => {
        const processedValues = {
            ...values,
            status: values.status === 'all' ? undefined : values.status,
            propertyUUID: values.propertyUUID === 'all' ? undefined : values.propertyUUID,
            tenantUUID: values.tenantUUID === 'all' ? undefined : values.tenantUUID,
            managerUUID: values.managerUUID === 'all' ? undefined : values.managerUUID,
            ownerUUID: values.ownerUUID === 'all' ? undefined : values.ownerUUID,
            search: values.search || undefined,
            page: 0,
        };
        onChange(processedValues);
    };

    const handleReset = () => {
        form.reset(DEFAULT_FILTERS);
        onChange(DEFAULT_FILTERS);
    };

    const handleStatusChange = (status: string | undefined) => {
        onChange({
            ...filters,
            status: status === 'all' ? undefined : status as "active" | "completed" | "canceled" | "pending" | undefined,
            page: 0,
        });
    };

    const handleSearchChange = (search: string) => {
        const updatedFilters = {
            ...filters,
            search: search || undefined,
            page: 0,
        };
        form.setValue('search', search);
        onChange(updatedFilters);
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 p-3 rounded-xl shadow-sm border">
                <div className="flex gap-4">
                    <FormField
                        control={form.control}
                        name="search"
                        render={({ field }) => (
                            <FormItem className="flex-1">
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/60" />
                                    <Input
                                        placeholder={t("leases.filters.searchPlaceholder")}
                                        className="pl-12"
                                        {...field}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                </div>
                            </FormItem>
                        )}
                    />
                    <Button
                        type="button"
                        variant={isFiltersVisible ? "secondary" : "outline"}
                        onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    >
                        <Filter className="h-4 w-4 mr-2" />
                        {t("leases.filters.title")}
                    </Button>
                </div>

                {isFiltersVisible && (
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-base font-semibold mb-4">{t("leases.filters.sections.mainFilters")}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                    control={form.control}
                                    name="status"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel>{t('leases.filters.status')}</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('leases.filters.statusPlaceholder')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="all">{t('common.all')}</SelectItem>
                                                    <SelectItem value="active">{t('leases.status.active')}</SelectItem>
                                                    <SelectItem value="pending">{t('leases.status.pending')}</SelectItem>
                                                    <SelectItem value="completed">{t('leases.status.completed')}</SelectItem>
                                                    <SelectItem value="canceled">{t('leases.status.canceled')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="propertyUUID"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel>{t('leases.filters.property')}</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('leases.filters.propertyPlaceholder')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="all">{t('common.all')}</SelectItem>
                                                    {properties.map((property) => (
                                                        <SelectItem key={property.uuid} value={property.uuid}>
                                                            {property.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="tenantUUID"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel>{t('leases.filters.tenant')}</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('leases.filters.tenantPlaceholder')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="all">{t('common.all')}</SelectItem>
                                                    {tenants.map((tenant) => (
                                                        <SelectItem key={tenant.uuid} value={tenant.uuid}>
                                                            {`${tenant.firstName} ${tenant.lastName}`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="managerUUID"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel>{t('leases.filters.manager')}</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('leases.filters.managerPlaceholder')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="all">{t('common.all')}</SelectItem>
                                                    {managers.map((manager) => (
                                                        <SelectItem key={manager.uuid} value={manager.uuid}>
                                                            {`${manager.firstName} ${manager.lastName}`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="ownerUUID"
                                    render={({ field }) => (
                                        <FormItem className="space-y-2">
                                            <FormLabel>{t('leases.filters.owner')}</FormLabel>
                                            <Select
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('leases.filters.ownerPlaceholder')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="all">{t('common.all')}</SelectItem>
                                                    {owners.map((owner) => (
                                                        <SelectItem key={owner.uuid} value={owner.uuid}>
                                                            {`${owner.firstName} ${owner.lastName}`}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleReset}
                            >
                                <X className="h-4 w-4 mr-2" />
                                {t('common.reset')}
                            </Button>
                            <Button type="submit">
                                <Filter className="h-4 w-4 mr-2" />
                                {t('common.apply')}
                            </Button>
                        </div>
                    </div>
                )}
            </form>
        </Form>
    );
};

export default LeasesFilters; 